import { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import GlossyIcon from "@/components/GlossyIcon";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { cn } from "@/lib/utils";

type Review = {
  id: string;
  reviewer_name: string;
  review_text: string;
  rating: number;
};

const Testimonials = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref, visible } = useScrollReveal();

  useEffect(() => {
    const fetchReviews = async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("id, reviewer_name, review_text, rating")
        .eq("featured", true);

      if (!error && data && data.length > 0) {
        const shuffled = [...data].sort(() => Math.random() - 0.5);
        setReviews(shuffled.slice(0, 3));
      }
      setLoading(false);
    };
    fetchReviews();
  }, []);

  return (
    <section
      ref={ref}
      className={cn(
        "py-20 bg-background transition-all duration-700 ease-out",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}
    >
      <div className="container">
        <div className={cn("text-center mb-3 transition-all duration-700 delay-100 ease-out", visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="font-display text-2xl font-bold text-primary">4.9</span>
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <GlossyIcon key={i} name="star" size={18} />
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground font-body">Based on 45 Google Reviews</p>
        </div>
        <div className={cn("text-center mb-12 transition-all duration-700 delay-200 ease-out", visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">Our Happy Customers</h2>
          <p className="text-muted-foreground font-body max-w-lg mx-auto">
            We understand confidence starts with comfort. See what our customers are saying.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-48 rounded" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <div
                key={r.id}
                className={cn(
                  "bg-secondary rounded p-8 relative product-card-hover transition-all duration-500 ease-out",
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                )}
                style={{ transitionDelay: visible ? `${300 + i * 100}ms` : "0ms" }}
              >
                <Quote className="w-8 h-8 text-icon-info/20 absolute top-6 right-6" />
                <div className="flex gap-1 mb-4">
                  {[...Array(r.rating)].map((_, i) => (
                    <GlossyIcon key={i} name="star" size={18} />
                  ))}
                </div>
                <p className="font-body text-sm leading-relaxed text-foreground/80 mb-6">"{r.review_text}"</p>
                <p className="font-display font-semibold text-sm">{r.reviewer_name}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
